import React from 'react';
import { FormHelperText } from '@mui/material';
import * as yup from 'yup';
import { ProviderCommissionType } from '@prisma/client';
import { LoadedUser, Option, Service } from '../../interfaces';
import { AdditionalServiceCheckoutItem } from '../../types/payments';
import { getArtistOptionsForService } from '../../services/business';
import { formatDollarAmount, isNil } from '../../utils/global/helper';
import { getBusinessCurrency } from '../../helpers/businesses';
import useAwaitedForm from '../useAwaitedForm';
import { DEFAULT_TATTOO_SERVICE_ID } from '../../constants/appointments';
import CashDiscountFormRow from '../../components/payments/CashDiscountFormRow';
import useCashDiscount from '../payments/useCashDiscount';

const serviceFormSchema = yup.object({
  price: yup
    .number()
    .transform((v, orig) =>
      orig === '' || orig == null ? undefined : Number(v),
    )
    .min(0, 'Price must be 0 or more')
    .required('Price is required'),
  employeeId: yup.string().optional(),
  providerCommissionType: yup
    .mixed<ProviderCommissionType>()
    .oneOf(Object.values(ProviderCommissionType))
    .optional(),
});

export type ServiceFormValues = yup.InferType<typeof serviceFormSchema>;

interface ShowServiceFormOptions {
  service: Service | null;
  user: LoadedUser | null;
  appointmentEmployeeId?: string | null;
  initialValues?: Partial<ServiceFormValues>;
  /** Minimum allowed price (e.g. deposit amount). Enforced in form validation. */
  minPrice?: number;
  title?: string;
  submitButtonText?: string;
  /** Disable the provider select input (e.g. when appointment provider is locked) */
  disableProviderSelect?: boolean;
  /** Existing deposit (dollars) — splits the fee per portion in CashDiscountFormRow. */
  depositPrice?: number;
  /** Selected payment method — drives terminal vs online rate/min fee. */
  paymentMethod?: string | null;
  /** How the existing deposit was paid — drives rate/min fee for its portion. */
  depositPaymentMethod?: string | null;
  /** Deposit's bundled fee (cents) from backend — overrides recalc. */
  depositBundledFeeCents?: number;
  /** Sum of (price × qty) for OTHER cart lines — drives proportional fee split. */
  groupOtherLinesTotal?: number;
  /** Offers removal from the same place the line is edited. */
  onRemove?: () => void;
  /** Defaults to removing the service from the cart. */
  removeLabel?: string;
}

export function buildServiceCheckoutItem(
  service: Service,
  values: ServiceFormValues,
  defaultEmployeeId: string,
): Omit<AdditionalServiceCheckoutItem, 'id'> {
  return {
    serviceId:
      service.serviceId === DEFAULT_TATTOO_SERVICE_ID
        ? null
        : service.serviceId,
    serviceName: service.serviceName,
    employeeId: values.employeeId || defaultEmployeeId,
    providerCommissionType:
      values.providerCommissionType ?? ProviderCommissionType.BASE,
    price: Number(values.price) || 0,
  };
}

export function useServiceFormModal() {
  const onAwaitForm = useAwaitedForm();
  const { attachedInputSx } = useCashDiscount();

  return React.useCallback(
    async ({
      service,
      user,
      appointmentEmployeeId,
      initialValues: overrideInitialValues,
      minPrice,
      title = 'Add service',
      submitButtonText = 'Add service',
      disableProviderSelect = false,
      depositPrice,
      paymentMethod,
      depositPaymentMethod,
      depositBundledFeeCents,
      groupOtherLinesTotal,
      onRemove,
      removeLabel,
    }: ShowServiceFormOptions): Promise<ServiceFormValues | null> => {
      if (!service || !user) return null;

      const serviceIdForApi =
        service.serviceId === DEFAULT_TATTOO_SERVICE_ID
          ? undefined
          : service.serviceId;

      const artistOptions: Option[] = getArtistOptionsForService({
        serviceId:
          serviceIdForApi != null ? String(serviceIdForApi) : undefined,
        user,
        addSelfIfEmpty: true,
      });

      const defaultEmployeeId = user.employee?.employeeId ?? user.userId ?? '';
      const appointmentEmployeeInList =
        appointmentEmployeeId &&
        artistOptions.some(o => o.value === appointmentEmployeeId);

      const getCommissionOptions = (employeeId: string): Option[] => {
        if (!user.business?.employees) return [];
        const employee = user.business.employees.find(
          e => e.employeeId === employeeId,
        );
        if (!employee) return [];

        let etsRate: number | null = null;
        if (user.business?.services && !isNil(serviceIdForApi)) {
          const svc = user.business.services.find(
            s => s.serviceId === serviceIdForApi,
          );
          const ets = svc?.employeesToServices?.find(
            e => e.employeeId === employeeId,
          );
          etsRate =
            ets?.serviceCommissionRate != null && ets.serviceCommissionRate >= 0
              ? ets.serviceCommissionRate
              : null;
        }

        const baseRate = (employee.paymentToEmployeePercent ?? 0) * 100;
        const options: Option[] = [];
        if (etsRate !== null) {
          options.push({
            label: `${(etsRate * 100).toFixed(0)}%`,
            value: ProviderCommissionType.SERVICE,
          });
        }
        options.push({
          label: `${baseRate}%`,
          value: ProviderCommissionType.BASE,
        });
        if (
          employee.payrollType === 'COMMISSION' &&
          employee.bonusCommissionPercent
        ) {
          const bonusRate =
            Math.min(
              (employee.paymentToEmployeePercent ?? 0) +
                employee.bonusCommissionPercent,
              1,
            ) * 100;
          options.push({
            label: `${bonusRate}%`,
            value: ProviderCommissionType.BONUS,
          });
        }
        return options;
      };

      const initialEmployeeId: string =
        overrideInitialValues?.employeeId ??
        (appointmentEmployeeInList
          ? (appointmentEmployeeId as string)
          : String(artistOptions[0]?.value ?? defaultEmployeeId));

      const initialCommissionOpts = getCommissionOptions(initialEmployeeId);
      const hasServiceRate = initialCommissionOpts.some(
        o => o.value === ProviderCommissionType.SERVICE,
      );
      const defaultCommissionType = hasServiceRate
        ? ProviderCommissionType.SERVICE
        : ProviderCommissionType.BASE;

      const initialEmployee = user.business.employees?.find(
        e => e.employeeId === initialEmployeeId,
      );
      const defaultPriceEmpty =
        !overrideInitialValues?.price &&
        service.serviceCategory === 'TATTOO' &&
        !service.serviceId &&
        initialEmployee?.defaultHourlyRate != null;
      const initialPrice: number | '' = overrideInitialValues?.price
        ? overrideInitialValues.price
        : defaultPriceEmpty
        ? ''
        : service.defaultPrice ?? 0;

      const result = await onAwaitForm<ServiceFormValues>({
        allowUntouched: true,
        form: {
          title,
          destructiveAction: onRemove && {
            label: removeLabel ?? 'Remove service',
            onClick: onRemove,
          },
          initialValues: {
            price: initialPrice,
            employeeId: initialEmployeeId,
            providerCommissionType:
              overrideInitialValues?.providerCommissionType ??
              defaultCommissionType,
          } as ServiceFormValues,
          validationSchema:
            minPrice && minPrice > 0
              ? serviceFormSchema.shape({
                  price: yup
                    .number()
                    .transform((v, orig) =>
                      orig === '' || orig == null ? undefined : Number(v),
                    )
                    .min(
                      minPrice,
                      `Price must be at least $${minPrice.toFixed(
                        2,
                      )} (deposit amount)`,
                    )
                    .required('Price is required'),
                })
              : serviceFormSchema,
          submitButtonText,
          inputRows: (values: ServiceFormValues) => {
            const priceValue = Number(values.price) || 0;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rows: any[] = [
              [
                {
                  inputType: 'number',
                  name: 'price',
                  label: 'Total price',
                  placeholder: defaultPriceEmpty ? 'Enter total price' : '0',
                  min: 0,
                  step: 0.01,
                  autoFocus: true,
                  suffix: getBusinessCurrency(user.business),
                  inputSx: priceValue > 0 ? attachedInputSx : undefined,
                },
              ],
              [
                {
                  inputType: 'node',
                  isVisible: !!attachedInputSx && priceValue > 0,
                  node: React.createElement(CashDiscountFormRow, {
                    price: {
                      type: 'FIXED',
                      amount: priceValue,
                    },
                    variant: 'attached',
                    depositPrice,
                    paymentMethod,
                    depositPaymentMethod,
                    depositBundledFeeCents,
                    groupOtherLinesTotal,
                  }),
                },
              ],
              [
                {
                  inputType: 'node',
                  isVisible: !!minPrice && minPrice > 0,
                  node: React.createElement(
                    FormHelperText,
                    { sx: { marginTop: -1 } },
                    `Minimum ${formatDollarAmount(
                      minPrice ?? 0,
                    )} (deposit already paid)`,
                  ),
                },
              ],
              [
                {
                  inputType: 'select',
                  name: 'employeeId',
                  label: 'Provider',
                  placeholder: 'Select provider',
                  options: artistOptions.map(o => ({
                    ...o,
                    label: o.label ?? '',
                  })),
                  disabled: disableProviderSelect,
                },
              ],
            ];
            const commissionOpts = getCommissionOptions(
              values.employeeId || defaultEmployeeId,
            );
            if (commissionOpts.length > 1) {
              rows.push([
                {
                  inputType: 'select',
                  name: 'providerCommissionType',
                  label: 'Provider Commission',
                  placeholder: 'Select commission',
                  options: commissionOpts.map(o => ({
                    ...o,
                    label: o.label ?? '',
                  })),
                },
              ]);
            }
            return rows;
          },
        },
      });

      if (result?.providerCommissionType === ProviderCommissionType.SERVICE) {
        const commissionOpts = getCommissionOptions(
          result.employeeId || defaultEmployeeId,
        );
        const submittedEmployeeHasServiceRate = commissionOpts.some(
          o => o.value === ProviderCommissionType.SERVICE,
        );
        if (!submittedEmployeeHasServiceRate) {
          return {
            ...result,
            providerCommissionType: ProviderCommissionType.BASE,
          };
        }
      }

      return result;
    },
    [onAwaitForm, attachedInputSx],
  );
}
